// Follows "HR Design rules.md": GEN, DIS, LOAD
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * Disabled (DIS-01, DIS-03, DIS-04): grey tokens, never opacity. The `!` makes the disabled colours win over
 * hover/active, so a disabled button never shows interaction colours. Applies to native `disabled` and to
 * `aria-disabled` (links rendered with asChild).
 */
const DIS_FILLED =
  'disabled:!bg-disabled disabled:!text-disabled-foreground aria-disabled:!bg-disabled aria-disabled:!text-disabled-foreground';
const DIS_OUTLINE =
  'disabled:!border-disabled-border disabled:!bg-disabled disabled:!text-disabled-foreground aria-disabled:!border-disabled-border aria-disabled:!bg-disabled aria-disabled:!text-disabled-foreground';
const DIS_TEXT =
  'disabled:!bg-transparent disabled:!text-disabled-foreground disabled:!no-underline aria-disabled:!bg-transparent aria-disabled:!text-disabled-foreground aria-disabled:!no-underline';

const buttonVariants = cva(
  "button-text-select inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow] duration-fast ease-standard focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:!shadow-none aria-disabled:cursor-not-allowed aria-disabled:!shadow-none aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: `bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active ${DIS_FILLED}`,
        // The gold brand colour (not a neutral grey); neutral buttons are outline or ghost.
        secondary: `bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-active ${DIS_FILLED}`,
        tertiary: `bg-tertiary text-tertiary-foreground hover:bg-tertiary-hover active:bg-tertiary-active ${DIS_FILLED}`,
        success: `bg-success text-success-foreground hover:bg-success-hover active:bg-success-active ${DIS_FILLED}`,
        warning: `bg-warning text-warning-foreground hover:bg-warning-hover active:bg-warning-active ${DIS_FILLED}`,
        destructive: `bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-active focus-visible:ring-destructive ${DIS_FILLED}`,
        outline: `border border-border bg-card shadow-sm hover:bg-accent hover:text-accent-foreground active:bg-primary-100 dark:active:bg-primary-900 ${DIS_OUTLINE}`,
        ghost: `hover:bg-accent hover:text-accent-foreground active:bg-primary-100 dark:active:bg-primary-900 ${DIS_TEXT}`,
        link: `text-primary underline-offset-4 hover:underline active:text-primary-active ${DIS_TEXT}`,
      },
      // GEN-03: 32px default, 28px small, 40px large.
      size: {
        default: 'h-8 px-3 has-[>svg]:px-2.5',
        sm: 'h-7 gap-1.5 px-2.5 has-[>svg]:px-2',
        lg: 'h-10 px-5 has-[>svg]:px-4',
        icon: 'size-8',
        'icon-sm': 'size-7',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  disabled,
  children,
  onClick,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** An action in progress (Save, Submit, Approve): shows the only spinner the rules allow (LOAD-02) and disables the button. */
    loading?: boolean;
  }) {
  const ariaDisabled = props['aria-disabled'] === true || props['aria-disabled'] === 'true';

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // DIS-05: an element disabled with aria-disabled (a link) must not navigate or fire.
    if (ariaDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Keeps button text selectable for the UILM extension: a click that ends a text selection does nothing.
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
  };

  const classes = cn(buttonVariants({ variant, size, className }));
  const state = {
    'data-slot': 'button',
    'data-variant': variant,
    'data-size': size,
    'data-loading': loading || undefined,
    'aria-busy': loading || undefined,
  };

  // Radix Slot requires exactly one React element child. A sibling `false` from
  // `{loading && ...}` makes `children` an array and throws in @radix-ui/react-slot@1.3+.
  if (asChild) {
    return (
      <Slot {...state} className={classes} {...props} onClick={handleClick}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      {...state}
      className={classes}
      disabled={disabled || loading}
      {...props}
      onClick={handleClick}
    >
      {loading ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export { Button, buttonVariants };
